import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { addDays, subYears } from 'date-fns'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import { useNavigate } from 'react-router-dom'

import { DocumentDocument } from '../../lib/db/db.types'
import { useAuth } from '../../store/hooks'
import {
  subscribeToDocuments,
  getDocumentDownloadUrl,
  deleteDocument,
  updateDocumentDetails,
} from '../../lib/db/db'
import { DocumentUpload } from './DocumentUpload'
import { toDateString } from '../../lib/shared/calendar.utils'
import { DocumentListItem } from './DocumentListItem'

type DocumentRepositoryProps = {
  workspaceId: string
  accountingReferenceDate: string
}

export const DocumentRepository = ({
  workspaceId,
  accountingReferenceDate,
}: DocumentRepositoryProps) => {
  const navigate = useNavigate()
  const { authUser } = useAuth()
  const [searchQuery, setSearchQuery] = useState('')
  const [documents, setDocuments] = useState<
    Array<{ id: string; data: DocumentDocument }>
  >([])
  const [documentToDelete, setDocumentToDelete] = useState<{
    id: string
    storageRef: string
    fileName: string
  } | null>(null)
  const [documentToSetYear, setDocumentToSetYear] = useState<{
    id: string
    name: string
    currentYear?: string
  } | null>(null)
  const [dropdownOpen, setDropdownOpen] = useState<string | null>(null)
  const [selectedYearOffset, setSelectedYearOffset] = useState(0)
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false)
  const [selectedDocuments, setSelectedDocuments] = useState<Set<string>>(
    new Set(),
  )
  const [isMassDeleteDialogOpen, setIsMassDeleteDialogOpen] = useState(false)

  useEffect(() => {
    return subscribeToDocuments({
      workspaceId,
      onUpdate: setDocuments,
    })
  }, [workspaceId])

  const handleDownload = async (storageRef: string) => {
    try {
      const url = await getDocumentDownloadUrl(storageRef)
      window.open(url, '_blank')
    } catch (error) {
      console.error('Error downloading file:', error)
      toast('Failed to download file')
    }
  }

  const handleDelete = async (
    documentId: string,
    storageRef: string,
    fileName: string,
  ) => {
    setDocumentToDelete({ id: documentId, storageRef, fileName })
  }

  const confirmDelete = async () => {
    if (!documentToDelete) return

    try {
      await deleteDocument({
        workspaceId,
        documentId: documentToDelete.id,
        storageRef: documentToDelete.storageRef,
      })
      toast(`${documentToDelete.fileName} deleted successfully`)
      setDocumentToDelete(null)
    } catch (error) {
      console.error('Error deleting file:', error)
      toast(`Failed to delete ${documentToDelete.fileName}`)
    }
  }

  const handleSetFinancialYear = (
    documentId: string,
    name: string,
    currentYear?: string,
  ) => {
    setDocumentToSetYear({ id: documentId, name, currentYear })
    setDropdownOpen(null)
  }

  const confirmSetYear = async (selectedDate: string) => {
    if (!documentToSetYear || !authUser) return

    try {
      await updateDocumentDetails({
        userId: authUser.id,
        workspaceId,
        documentId: documentToSetYear.id,
        details: {
          fiscalPeriod: {
            startDate: toDateString(
              subYears(addDays(new Date(selectedDate), 1), 1),
            ),
            endDate: selectedDate,
          },
        },
      })
      toast(`Financial year updated for ${documentToSetYear.name}`)
      setDocumentToSetYear(null)
    } catch (error) {
      console.error('Error updating financial year:', error)
      toast(`Failed to update financial year for ${documentToSetYear.name}`)
    }
  }

  const getFinancialYearDates = (offset = 0) => {
    const endDate = new Date(accountingReferenceDate)
    endDate.setFullYear(endDate.getFullYear() + offset)

    const startDate = new Date(endDate)
    startDate.setFullYear(startDate.getFullYear() - 1)
    startDate.setDate(startDate.getDate() + 1)

    return {
      startDate: toDateString(startDate),
      endDate: toDateString(endDate),
    }
  }

  // Filter documents before grouping
  const filteredDocuments = documents.filter((doc) =>
    doc.data.name.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  const { groupedDocuments, ungroupedDocuments } = filteredDocuments.reduce(
    (acc, doc) => {
      if (doc.data.fiscalPeriod?.endDate) {
        const yearKey = `${doc.data.fiscalPeriod.startDate}|${doc.data.fiscalPeriod.endDate}`
        if (!acc.groupedDocuments[yearKey]) acc.groupedDocuments[yearKey] = []
        acc.groupedDocuments[yearKey].push(doc)
      } else {
        acc.ungroupedDocuments.push(doc)
      }
      return acc
    },
    {
      groupedDocuments: {} as Record<string, typeof documents>,
      ungroupedDocuments: [] as typeof documents,
    },
  )

  // Get the current year and calculate how many years to show
  const currentDate = new Date()
  const referenceDate = new Date(accountingReferenceDate)
  const yearsToShow =
    currentDate.getFullYear() - referenceDate.getFullYear() + 1

  // Add empty arrays for all years up to current
  for (let i = 0; i < yearsToShow; i++) {
    const { startDate, endDate } = getFinancialYearDates(i)
    const yearKey = `${startDate}|${endDate}`
    if (!groupedDocuments[yearKey]) {
      if (!searchQuery) {
        groupedDocuments[yearKey] = []
      }
    }
  }

  // Sort year keys in reverse chronological order
  const sortedYearKeys = Object.keys(groupedDocuments).sort((a, b) => {
    const endDateA = a.split('|')[1]
    const endDateB = b.split('|')[1]
    return new Date(endDateB).getTime() - new Date(endDateA).getTime()
  })

  const onDragEnd = async (result: any) => {
    if (!result.destination || !authUser) return

    const sourceYear = result.source.droppableId
    const targetYear = result.destination.droppableId
    const documentId = result.draggableId

    if (sourceYear === targetYear) return

    try {
      const document = documents.find((doc) => doc.id === documentId)
      if (!document) return

      const [, targetEndDate] = targetYear.split('|')

      await updateDocumentDetails({
        userId: authUser.id,
        workspaceId,
        documentId,
        details: {
          fiscalPeriod: targetYear.startsWith('ungrouped')
            ? null
            : {
                startDate: toDateString(
                  subYears(addDays(new Date(targetEndDate), 1), 1),
                ),
                endDate: targetEndDate,
              },
        },
      })
      toast('Document moved successfully')
    } catch (error) {
      console.error('Error moving document:', error)
      toast('Failed to move document')
    }
  }

  // Add new handlers for mass edit functionality
  const toggleMultiSelectMode = () => {
    setIsMultiSelectMode(!isMultiSelectMode)
    setSelectedDocuments(new Set())
  }

  const toggleDocumentSelection = (documentId: string) => {
    const newSelection = new Set(selectedDocuments)
    if (newSelection.has(documentId)) {
      newSelection.delete(documentId)
    } else {
      newSelection.add(documentId)
    }
    setSelectedDocuments(newSelection)
  }

  const handleMassDelete = async () => {
    if (selectedDocuments.size === 0) return

    try {
      // Create array of promises for each document deletion
      const deletePromises = documents
        .filter((doc) => selectedDocuments.has(doc.id))
        .map((doc) =>
          deleteDocument({
            workspaceId,
            documentId: doc.id,
            storageRef: doc.data.storageRef,
          }),
        )

      await Promise.all(deletePromises)
      toast(`${selectedDocuments.size} documents deleted successfully`)
      setSelectedDocuments(new Set())
      setIsMassDeleteDialogOpen(false)
      setIsMultiSelectMode(false)
    } catch (error) {
      console.error('Error deleting files:', error)
      toast('Failed to delete some documents')
    }
  }

  const handleSignDocument = (documentId: string) => {
    navigate(`/workspace/${workspaceId}/documents/${documentId}/sign`)
  }

  return (
    <div>
      {authUser && (
        <DocumentUpload workspaceId={workspaceId} userId={authUser.id} />
      )}

      <div className="mt-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Uploaded Documents</h3>
          <button
            onClick={toggleMultiSelectMode}
            className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50"
          >
            {isMultiSelectMode ? 'Exit Selection' : 'Select Multiple'}
          </button>
        </div>

        {/* Show mass actions when in multi-select mode */}
        {isMultiSelectMode && selectedDocuments.size > 0 && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg flex justify-between items-center">
            <span>{selectedDocuments.size} documents selected</span>
            <button
              onClick={() => setIsMassDeleteDialogOpen(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Delete Selected
            </button>
          </div>
        )}

        {/* Add search input */}
        <div className="mb-4">
          <input
            type="text"
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <DragDropContext onDragEnd={onDragEnd}>
          {/* Ungrouped documents section */}
          {ungroupedDocuments.length > 0 && (
            <Droppable droppableId={`ungrouped|null`}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`mb-8 p-4 rounded-lg transition-colors ${
                    snapshot.isDraggingOver ? 'bg-gray-100' : ''
                  }`}
                >
                  <h4 className="text-md font-medium mb-4 text-gray-700">
                    Unassigned Documents
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {ungroupedDocuments.map(({ id, data }, index) => (
                      <Draggable key={id} draggableId={id} index={index}>
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <DocumentListItem
                              id={id}
                              data={data}
                              dropdownOpen={dropdownOpen}
                              onToggleDropdown={setDropdownOpen}
                              onSetFinancialYear={handleSetFinancialYear}
                              onDelete={handleDelete}
                              onDownload={handleDownload}
                              accountingReferenceDate={accountingReferenceDate}
                              isMultiSelectMode={isMultiSelectMode}
                              isSelected={selectedDocuments.has(id)}
                              onToggleSelect={() => toggleDocumentSelection(id)}
                              onSignDocument={handleSignDocument}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                </div>
              )}
            </Droppable>
          )}

          {/* Grouped documents section */}
          {sortedYearKeys.map((yearKey) => {
            const [startDate, endDate] = yearKey.split('|')
            return (
              <Droppable key={yearKey} droppableId={`${yearKey}`}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`mb-8 p-4 rounded-lg transition-colors relative ${
                      snapshot.isDraggingOver ? 'bg-gray-100 z-10' : 'z-0'
                    }`}
                  >
                    <h4 className="text-md font-medium mb-4 text-gray-700">
                      Financial Year {startDate} to {endDate}
                    </h4>
                    <div
                      className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ${
                        groupedDocuments[yearKey].length === 0
                          ? 'min-h-[100px] flex items-center justify-center'
                          : 'min-h-[100px]'
                      }`}
                    >
                      {groupedDocuments[yearKey].length === 0 ? (
                        <p className="text-gray-500 text-center">
                          No documents assigned to this financial year.
                        </p>
                      ) : (
                        groupedDocuments[yearKey].map(({ id, data }, index) => (
                          <Draggable key={id} draggableId={id} index={index}>
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <DocumentListItem
                                  id={id}
                                  data={data}
                                  dropdownOpen={dropdownOpen}
                                  onToggleDropdown={setDropdownOpen}
                                  onSetFinancialYear={handleSetFinancialYear}
                                  onDelete={handleDelete}
                                  onDownload={handleDownload}
                                  accountingReferenceDate={
                                    accountingReferenceDate
                                  }
                                  isMultiSelectMode={isMultiSelectMode}
                                  isSelected={selectedDocuments.has(id)}
                                  onToggleSelect={() =>
                                    toggleDocumentSelection(id)
                                  }
                                  onSignDocument={handleSignDocument}
                                />
                              </div>
                            )}
                          </Draggable>
                        ))
                      )}
                      {provided.placeholder}
                    </div>
                  </div>
                )}
              </Droppable>
            )
          })}
        </DragDropContext>
      </div>

      {documentToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-medium mb-4">Confirm Delete</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete {documentToDelete.fileName}?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDocumentToDelete(null)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {documentToSetYear && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium mb-4">Set Financial Year</h3>
            <p className="text-gray-600 mb-4">
              Select the financial year for {documentToSetYear.name}
            </p>
            <div className="flex items-center justify-between mb-6">
              <button
                onClick={() => setSelectedYearOffset((prev) => prev - 1)}
                className="px-3 py-1 border rounded hover:bg-gray-50"
              >
                Previous
              </button>
              <div className="text-center flex-grow mx-4">
                <div className="text-sm text-gray-600">
                  {new Date(
                    getFinancialYearDates(selectedYearOffset).startDate,
                  ).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}{' '}
                  to
                </div>
                <div className="text-sm text-gray-600">
                  {new Date(
                    getFinancialYearDates(selectedYearOffset).endDate,
                  ).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </div>
              </div>
              <button
                onClick={() => setSelectedYearOffset((prev) => prev + 1)}
                className="px-3 py-1 border rounded hover:bg-gray-50"
              >
                Next
              </button>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setDocumentToSetYear(null)
                  setSelectedYearOffset(0)
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const { endDate } = getFinancialYearDates(selectedYearOffset)
                  confirmSetYear(endDate)
                  setSelectedYearOffset(0)
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add mass delete confirmation dialog */}
      {isMassDeleteDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-medium mb-4">Confirm Mass Delete</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete {selectedDocuments.size}{' '}
              documents?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setIsMassDeleteDialogOpen(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleMassDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Delete All
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
